import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  BulkSubcategoryIdsPayload,
  BulkSubcategoryStatusPayload,
  CreateSubcategoryPayload,
  PaginatedSubcategories,
  ReorderPayload,
  Subcategory,
  SubcategoryListParams,
  UpdateSubcategoryPayload,
} from '@/types/subcategory';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildSubcategoryListQuery(params: SubcategoryListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.categoryIds?.length) searchParams.set('categoryIds', params.categoryIds.join(','));

  return searchParams.toString();
}

export const subcategoriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubcategories: builder.query<PaginatedSubcategories, SubcategoryListParams>({
      query: (params) => ({
        url: `/subcategories?${buildSubcategoryListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildSubcategoryListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildSubcategoryListQuery(currentArg ?? {}) !== buildSubcategoryListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Subcategories' as const, id })),
              { type: 'Subcategories', id: 'LIST' },
            ]
          : [{ type: 'Subcategories', id: 'LIST' }],
    }),
    getSubcategory: builder.query<Subcategory, string>({
      query: (id) => `/subcategories/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Subcategories', id }],
    }),
    createSubcategory: builder.mutation<Subcategory, CreateSubcategoryPayload>({
      query: (body) => ({ url: '/subcategories/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Subcategories', id: 'LIST' }],
    }),
    updateSubcategory: builder.mutation<
      Subcategory,
      { id: string; body: UpdateSubcategoryPayload }
    >({
      query: ({ id, body }) => ({ url: `/subcategories/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Subcategories', id },
        { type: 'Subcategories', id: 'LIST' },
      ],
    }),
    deleteSubcategory: builder.mutation<void, string>({
      query: (id) => ({ url: `/subcategories/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Subcategories', id: 'LIST' }],
    }),
    reorderSubcategories: builder.mutation<Subcategory[], ReorderPayload>({
      query: (body) => ({ url: '/subcategories/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Subcategories', id: 'LIST' }],
    }),
    bulkUpdateSubcategoryStatus: builder.mutation<{ count: number }, BulkSubcategoryStatusPayload>({
      query: (body) => ({ url: '/subcategories/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Subcategories', id: 'LIST' }],
    }),
    bulkDeleteSubcategories: builder.mutation<{ count: number }, BulkSubcategoryIdsPayload>({
      query: (body) => ({ url: '/subcategories/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Subcategories', id: 'LIST' }],
    }),
    uploadSubcategoryImage: builder.mutation<{ image: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/subcategories/upload-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ image: string }>(json) ? json.data : (json as { image: string });
          const message = isApiResponse<{ image: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetSubcategoriesQuery,
  useLazyGetSubcategoriesQuery,
  useGetSubcategoryQuery,
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
  useReorderSubcategoriesMutation,
  useBulkUpdateSubcategoryStatusMutation,
  useBulkDeleteSubcategoriesMutation,
  useUploadSubcategoryImageMutation,
} = subcategoriesApi;
